import { NativeStackScreenProps } from '@react-navigation/native-stack';
import React from 'react';
import { useQuery } from 'react-query';
import { practiceOptions } from 'src/hooks/react-query/usePractice';
import { useAuth } from 'src/providers/AuthProvider';
import RootStackParamList from 'src/routes/stacks/RootStackNavigator/ParamsList';
import { Screens } from 'src/routes/stacks/screens';
import { BookingProvider } from './BookingProvider';
import { ActivityIndicator } from 'react-native-paper';
import { useAppTheme } from 'src/providers/AppThemeProvider';
import { getPatient, QueryKeys } from 'src/api';
import { unauthenticatedApiClient } from 'src/utils/axios/unauthenticated';
import { endpoint } from 'src/constants';
import { Client, Patient } from 'src/interfaces';
import { isDefinedOrThrowServerError } from 'src/utils';
import { getAppointmentRequestSettings } from './BookingProvider/helper';
import Practice from 'src/interfaces/api/Practice';
import OBLayoutController from './components/OBLayoutController';
import GoogleAnalytics from 'src/providers/GoogleTagManager';
import { usePracticeInfo } from 'src/hooks';
import { useTranslation } from 'react-i18next';
import Toast from 'react-native-toast-message';
import FeatureUnavailable from 'src/module/FormKit/components/FeatureUnavailable';

const PLACEHOLDER_TOKEN_DATA = {
  practice: undefined,
  client: undefined,
  patient: undefined
};

const OnlineBooking: React.FC<
  NativeStackScreenProps<RootStackParamList, Screens.ONLINE_BOOKING>
> = ({ route, navigation }) => {
  const { _user } = useAuth();
  const { colors } = useAppTheme();
  const practiceName = route.params?.practice;
  const patientId = route.params?.patientId;
  const token = route.params?.token;
  const { t } = useTranslation('onlineBooking');
  useQuery({
    ...practiceOptions(_user?.practiceId),
    onSuccess: (data) => {
      navigation.setParams({ practice: data.shortName });
    },

    enabled: !practiceName && !!_user && !token
  });

  const {
    data: { client, patient } = PLACEHOLDER_TOKEN_DATA,
    isError: autoFillError,
    isFetched: isTokenDataFetched
  } = useQuery({
    queryKey: [QueryKeys.AUTOFILL_APT, token],
    queryFn: async () => {
      const response = await unauthenticatedApiClient.get<{
        practice: Practice;
        client: Client;
        patient: Patient;
      }>(endpoint('AUTO_FILL_TOKEN', { token }), { params: { type: 'autofill' } });
      const data = isDefinedOrThrowServerError(response.data);
      navigation.setParams({ practice: data.practice.shortName });
      return data;
    },
    onError: () => {
      Toast.show({
        type: 'error',
        text1: t('errors.tokenExpired'),
        text2: t('errors.tokenExpiredDesc')
      });
    },
    cacheTime: Infinity,
    staleTime: 0,
    enabled: !!token
  });

  const patientQuery = useQuery({
    queryKey: [QueryKeys.PATIENT, patientId, _user?.sourceId],
    queryFn: async () => getPatient(_user!.sourceId, patientId!),
    enabled: !!patientId && !!_user?.sourceId && !token
  });

  const {
    data: practiceInfo,
    isFetched: practiceInfoIsFetched,
    isError
  } = usePracticeInfo(practiceName, {
    retry: false,
    enabled: !!practiceName && (!token || isTokenDataFetched)
  });

  const { data: requestSetting, isFetched: requestSettingsIsFetched } = useQuery({
    queryKey: [QueryKeys.REQUEST_SETTING, practiceInfo?.practiceId, 'online-booking'],
    queryFn: async () => {
      try {
        const response = await unauthenticatedApiClient.get(
          endpoint('GET_REQUEST_SETTING', { practiceId: practiceInfo?.practiceId })
        );
        const data = isDefinedOrThrowServerError(response.data);
        return getAppointmentRequestSettings(data);
      } catch {}
    },
    enabled: !!practiceInfo
  });

  const practiceId = practiceInfo?.practiceId ?? _user?.practiceId;

  if (
    !practiceName ||
    (!isError &&
      !autoFillError &&
      ((token && !isTokenDataFetched) ||
        (practiceName && (!practiceInfoIsFetched || !requestSettingsIsFetched))))
  ) {
    return <ActivityIndicator color={colors.primary} size={'large'} style={{ flex: 1 }} />;
  }

  if (
    !practiceId ||
    !practiceInfo ||
    isError ||
    !requestSetting ||
    requestSetting.appointmentRequestsDisabled
  ) {
    return (
      <FeatureUnavailable
        titleTranslation='featureUnavailable.appointmentRequests.title'
        messageTranslation='featureUnavailable.appointmentRequests.text'
      />
    );
  }

  return (
    <GoogleAnalytics practiceShortName={practiceName}>
      <BookingProvider
        practiceInfo={practiceInfo}
        user={client ?? _user}
        requestSetting={requestSetting}
        patient={patient ?? patientQuery?.data}
      >
        <OBLayoutController />
      </BookingProvider>
    </GoogleAnalytics>
  );
};

export default OnlineBooking;
